/**
 * The Social page's rows, built from a profile's own YouTube subscriptions (build guide
 * docs/social-connect-youtube.md, Phase 2c). Pure: the page calls buildSocialRows when it opens
 * and when the profile changes, and never again while it is open, so a refresh never reorders
 * what the viewer is looking at ("Live first", rule 5).
 *
 * Rows: "Latest from your subscriptions" (one video per channel, newest first, up to 24), then
 * topic rows from YouTube's own channel topics. Whatever is live comes first in every row; there
 * is no separate live row (owner, 2026-10-07). A channel appears in at most one topic row (the
 * first that matches, in the order below) and also in Latest, which is intended.
 */

export interface SubscriptionChannel {
  id: string;
  title: string;
  thumb?: string;
  /** Wikipedia topic names from YouTube's channel topicDetails, e.g. "Music", "Association_football". */
  topics: string[];
}

export interface FeedVideo {
  videoId: string;
  title: string;
  publishedAt: string;
  thumbnail: string;
}

/** The server's answer for one channel (services/api/src/social-youtube.ts). */
export interface ChannelAnswer {
  id: string;
  title: string;
  latest: FeedVideo | null;
  live: FeedVideo | null;
}

export interface SocialRow {
  id: string;
  title: string;
  /** Channel ids, in the order shown. */
  channelIds: string[];
}

export const LATEST_ROW_ID = "latest";
export const LATEST_CAP = 24;

const SPORTS = new Set(
  [
    "Sport", "Association_football", "American_football", "Basketball", "Baseball", "Boxing", "Golf",
    "Ice_hockey", "Mixed_martial_arts", "Motorsport", "Professional_wrestling", "Tennis", "Volleyball", "Cricket",
  ].map((name) => name.toLowerCase()),
);

interface TopicRow {
  id: string;
  title: string;
  matches: (topic: string) => boolean;
}

/** In this order; a channel takes the first row it matches. */
export const TOPIC_ROWS: TopicRow[] = [
  { id: "sports", title: "Sports", matches: (t) => SPORTS.has(t) },
  { id: "music", title: "Music", matches: (t) => t === "music" || t.endsWith("_music") },
  { id: "gaming", title: "Gaming", matches: (t) => t === "video_game_culture" || t.endsWith("_game") },
  { id: "movies", title: "Movies & TV", matches: (t) => t === "film" || t === "television_program" || t === "entertainment" },
  { id: "fashion", title: "Fashion & Beauty", matches: (t) => t === "fashion" || t === "physical_attractiveness" },
  { id: "news", title: "News & Talk", matches: (t) => t === "politics" || t === "society" },
  {
    id: "food",
    title: "Food & Lifestyle",
    matches: (t) => t === "food" || t === "lifestyle_(sociology)" || t === "tourism" || t === "hobby" || t === "pet",
  },
  {
    id: "knowledge",
    title: "Knowledge",
    matches: (t) => t === "knowledge" || t === "technology" || t === "health" || t === "military" || t === "religion",
  },
];

export const OTHER_ROW = { id: "other", title: "Everything else" };

/**
 * The rows of a profile that has not connected YouTube: the starter creators (creators.ts), one
 * row per category in the list's own order, live first, rows with nothing to show left out.
 */
export function starterRows(
  creators: ReadonlyArray<{ channelId: string; category: string }>,
  categories: ReadonlyArray<{ id: string; title: string }>,
  answers: ReadonlyMap<string, ChannelAnswer>,
  liveChecked: boolean,
): SocialRow[] {
  const isLive = (id: string) => liveChecked && Boolean(answers.get(id)?.live);
  const hasVideo = (id: string) => {
    const answer = answers.get(id);
    return Boolean(answer && (answer.latest || (liveChecked && answer.live)));
  };
  const rows: SocialRow[] = [];
  for (const category of categories) {
    const ids = creators.filter((c) => c.category === category.id && hasVideo(c.channelId)).map((c) => c.channelId);
    if (ids.length === 0) continue;
    rows.push({ id: category.id, title: category.title, channelIds: [...ids.filter(isLive), ...ids.filter((id) => !isLive(id))] });
  }
  return rows;
}

/** The topic names of YouTube's `topicCategories` (Wikipedia URLs): the last path segment, decoded. */
export function topicNames(topicCategories: readonly string[] | undefined): string[] {
  const names: string[] = [];
  for (const url of topicCategories ?? []) {
    const last = url.split("?")[0]!.split("#")[0]!.replace(/\/+$/, "").split("/").pop();
    if (!last) continue;
    try {
      names.push(decodeURIComponent(last));
    } catch {
      names.push(last);
    }
  }
  return names;
}

/** Which topic row a channel belongs to. */
export function topicRowOf(topics: readonly string[]): { id: string; title: string } {
  const lowered = topics.map((topic) => topic.toLowerCase());
  for (const row of TOPIC_ROWS) {
    if (lowered.some((topic) => row.matches(topic))) return { id: row.id, title: row.title };
  }
  return OTHER_ROW;
}

/**
 * The rows, empty ones left out.
 *
 * `liveChecked` false (no key, quota refused, an error) means nothing is treated as live: no
 * ordering by it, because a stale LIVE is worse than none.
 */
export function buildSocialRows(
  channels: readonly SubscriptionChannel[],
  answers: ReadonlyMap<string, ChannelAnswer> | readonly ChannelAnswer[],
  liveChecked: boolean,
): SocialRow[] {
  const byId = answers instanceof Map ? answers : new Map((answers as readonly ChannelAnswer[]).map((a) => [a.id, a]));
  const isLive = (id: string) => liveChecked && Boolean(byId.get(id)?.live);
  const hasVideo = (id: string) => {
    const answer = byId.get(id);
    return Boolean(answer && (answer.latest || (liveChecked && answer.live)));
  };
  const newestOf = (id: string): number => {
    const video = byId.get(id)?.latest;
    const time = video ? Date.parse(video.publishedAt) : NaN;
    return Number.isFinite(time) ? time : 0;
  };

  const withVideos = channels.filter((channel) => hasVideo(channel.id));
  const rows: SocialRow[] = [];

  // Latest: every live channel first (none cut), then the newest uploads up to the cap.
  const live = withVideos.filter((channel) => isLive(channel.id));
  const rest = withVideos
    .filter((channel) => !isLive(channel.id))
    .map((channel, position) => ({ channel, position }))
    .sort((a, b) => newestOf(b.channel.id) - newestOf(a.channel.id) || a.position - b.position)
    .map((entry) => entry.channel);
  const latest = [...live, ...rest.slice(0, Math.max(0, LATEST_CAP - live.length))];
  if (latest.length > 0) {
    rows.push({ id: LATEST_ROW_ID, title: "Latest from your subscriptions", channelIds: latest.map((c) => c.id) });
  }

  // Topic rows: each channel in the first row it matches; live first, then the subscription order.
  const buckets = new Map<string, { title: string; ids: string[] }>();
  for (const channel of withVideos) {
    const row = topicRowOf(channel.topics);
    const bucket = buckets.get(row.id) ?? { title: row.title, ids: [] };
    bucket.ids.push(channel.id);
    buckets.set(row.id, bucket);
  }
  for (const row of [...TOPIC_ROWS, OTHER_ROW]) {
    const bucket = buckets.get(row.id);
    if (!bucket) continue;
    const ordered = [...bucket.ids.filter((id) => isLive(id)), ...bucket.ids.filter((id) => !isLive(id))];
    rows.push({ id: row.id, title: row.title, channelIds: ordered });
  }
  return rows;
}
